import { useEffect, useState } from "react";
import {
  Drawer,
  Spin,
  Empty,
  Typography,
  Tag,
  Divider,
  Row,
  Col,
  Collapse,
  Descriptions,
} from "antd";
import moment from "moment";
import { postReq } from "../api";

const { Title, Paragraph, Text, Link } = Typography;
const { Panel } = Collapse;

const formatDate = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm") : "-");

const SectionTitle = ({ children }) => (
  <Title level={5} style={{ marginTop: 0, marginBottom: 12, color: "#13487D" }}>
    {children}
  </Title>
);

const JobPostingDetailDrawer = ({ jobId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !jobId) return;

    let cancelled = false;
    setData(null);
    setLoading(true);

    postReq("common/get_jobposting", { jobId })
      .then((res) => {
        if (cancelled) return;
        if (res.status === 1) {
          setData(res.data);
        } else {
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [jobId, open]);

  const location = data?.location || {};
  const locationText =
    [location.locality, location.city, location.state, location.country]
      .filter(Boolean)
      .join(", ") ||
    location.displayText ||
    "-";

  const salary = data?.salary || {};
  const salaryText =
    salary.min || salary.max
      ? `${salary.min ?? "-"} - ${salary.max ?? "-"} ${salary.currency || ""} ${salary.period ? `/ ${salary.period}` : ""}`
      : "-";

  const intelligence = data?.intelligence || {};
  const structured = intelligence.structured || {};
  const suggestion = intelligence.suggestion || {};

  const source = data?.source || {};

  return (
    <Drawer
      title={loading ? "Loading job posting..." : data?.title || "Job Posting"}
      width="75%"
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin />
        </div>
      )}

      {!loading && !data && (
        <Empty description="Unable to load job posting details" />
      )}

      {!loading && data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Header */}
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              {data.title}
            </Title>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Text strong>{data.company?.name || "-"}</Text>
              {data.company?.website && (
                <Link href={data.company.website} target="_blank" rel="noreferrer">
                  {data.company.website}
                </Link>
              )}
              <Tag color="blue">{data.jobId}</Tag>
              <Tag>{source.name || "-"}</Tag>
              <Tag color={data.status?.isActive ? "green" : "default"}>
                {data.status?.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Key facts */}
          <div>
            <SectionTitle>Key Facts</SectionTitle>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Location">{locationText}</Descriptions.Item>
              <Descriptions.Item label="Remote Type">
                {data.employment?.remoteType || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Contract Type">
                {data.employment?.contractType || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Contract Time">
                {data.employment?.contractTime || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Salary">{salaryText}</Descriptions.Item>
              <Descriptions.Item label="Posted At">
                {formatDate(data.postedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="First Seen">
                {formatDate(data.firstSeenAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Seen">
                {formatDate(data.lastSeenAt)}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* Description */}
          <div>
            <SectionTitle>Description</SectionTitle>
            <Paragraph style={{ whiteSpace: "pre-wrap" }}>
              {data.description?.text || "-"}
            </Paragraph>
          </div>

          {/* Categories */}
          {data.categories?.length > 0 && (
            <div>
              <SectionTitle>Categories</SectionTitle>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {data.categories.map((c, i) => (
                  <Tag key={i}>{c.label || c.tag}</Tag>
                ))}
              </div>
            </div>
          )}

          {/* Human decision */}
          <div>
            <SectionTitle>Human Decision</SectionTitle>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="ICP">{data.icp || "pending"}</Descriptions.Item>
              <Descriptions.Item label="ICP By">
                {data.icpBy?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Relevant">
                {data.relevant || "pending"}
              </Descriptions.Item>
              <Descriptions.Item label="Relevant By">
                {data.relevantBy?.name || "-"}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* AI Intelligence */}
          <div>
            <SectionTitle>AI Intelligence (Advisory)</SectionTitle>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Status">
                {intelligence.status || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Model">
                {intelligence.model || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Suggested ICP">
                {suggestion.icp
                  ? `${suggestion.icp} (${suggestion.icpConfidence ?? "-"})`
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Suggested Relevant">
                {suggestion.relevant
                  ? `${suggestion.relevant} (${suggestion.relevantConfidence ?? "-"})`
                  : "-"}
              </Descriptions.Item>
            </Descriptions>

            {(suggestion.icpReasons?.length > 0 ||
              suggestion.relevantReasons?.length > 0) && (
              <Row gutter={16} style={{ marginBottom: 12 }}>
                <Col span={12}>
                  <Text strong>Why ICP / Non_ICP:</Text>
                  {suggestion.icpReasons?.length > 0 ? (
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                      {suggestion.icpReasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  ) : (
                    <div>-</div>
                  )}
                </Col>
                <Col span={12}>
                  <Text strong>Why Relevant / Non-relevant:</Text>
                  {suggestion.relevantReasons?.length > 0 ? (
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                      {suggestion.relevantReasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  ) : (
                    <div>-</div>
                  )}
                </Col>
              </Row>
            )}

            {structured.summary && (
              <Paragraph>
                <Text strong>Summary: </Text>
                {structured.summary}
              </Paragraph>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Must Have Skills: </Text>
                <div>
                  {(structured.skills?.mustHave || []).map((s, i) => (
                    <Tag key={i}>{s}</Tag>
                  ))}
                  {!structured.skills?.mustHave?.length && "-"}
                </div>
              </Col>
              <Col span={12}>
                <Text strong>Nice To Have: </Text>
                <div>
                  {(structured.skills?.niceToHave || []).map((s, i) => (
                    <Tag key={i}>{s}</Tag>
                  ))}
                  {!structured.skills?.niceToHave?.length && "-"}
                </div>
              </Col>
            </Row>

            <Descriptions bordered column={2} size="small" style={{ marginTop: 12 }}>
              <Descriptions.Item label="Experience">
                {structured.experience?.min ?? "-"} - {structured.experience?.max ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Domain">
                {structured.domain || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Seniority">
                {structured.seniority || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Work Mode">
                {structured.workMode || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Urgency">
                {structured.urgency || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">
                {structured.startDate || "-"}
              </Descriptions.Item>
            </Descriptions>

            {structured.responsibilities?.length > 0 && (
              <Paragraph style={{ marginTop: 12 }}>
                <Text strong>Responsibilities:</Text>
                <ul>
                  {structured.responsibilities.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </Paragraph>
            )}
          </div>

          {/* Source & provenance */}
          <div>
            <SectionTitle>Source & Provenance</SectionTitle>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Source Name">
                {source.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Source Job Id">
                {source.jobId || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Source URL" span={2}>
                {source.url ? (
                  <Link href={source.url} target="_blank" rel="noreferrer">
                    {source.url}
                  </Link>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Fingerprint">
                {data.dedupe?.fingerprint || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Canonical Key">
                {data.dedupe?.canonicalKey || "-"}
              </Descriptions.Item>
            </Descriptions>

            {data.sourcesSeen?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Text strong>Also seen on:</Text>
                <ul>
                  {data.sourcesSeen.map((s, i) => (
                    <li key={i}>
                      {s.name} ({s.jobId}) - first: {formatDate(s.firstSeenAt)}, last:{" "}
                      {formatDate(s.lastSeenAt)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Raw data */}
          <Collapse>
            <Panel header="Raw / Source Meta (Advanced)" key="raw">
              <pre style={{ maxHeight: 400, overflow: "auto", fontSize: 12 }}>
                {JSON.stringify(
                  { sourceMeta: data.sourceMeta, raw: data.raw },
                  null,
                  2,
                )}
              </pre>
            </Panel>
          </Collapse>
        </div>
      )}
    </Drawer>
  );
};

export default JobPostingDetailDrawer;
